/**
 * useArmouryShelves - "My library" on a weapon's shelves, from the GM's Assets
 * library (plan armoury, stage 4).
 *
 * The area and effect shelves are synchronous lists (a pick applies at once),
 * so the library's area templates and effects are READ AHEAD: each asset's
 * current version once, cached by id and version, so a library change re-reads
 * only what changed. A save is a new asset in "From session <code>" and appears
 * when the library's watcher brings it back.
 *
 * Null without a library (a player, an account that cannot host): the shelves
 * then keep this browser's lists, and say so.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { newFromTable, readArmouryAsset, type LibraryAccess } from '../assetSession';
import { KINDS } from '../engine/assets/kinds';
import type { AssetRow, Origin } from '../engine/assets/types';
import type { Module } from '../engine/weaponEditor/modules';
import type { Loadout } from '../engine/roster/types';
import type { WeaponArea } from '../engine/areaShapes';
import type { EffectKind } from '../engine/weaponEffectList';
import type { AreaPreset } from '../utils/areaPalette';
import type { EffectPreset } from '../utils/effectPalette';
import { diag, errorSummary } from '../diagnostics/log';
import useStableCallback from './useStableCallback';
import useStableList from './useStableList';

/** A library module on the forge's shelf: its asset, for the recipe's origin. */
interface ShelvedModule {
  id: string;
  name: string;
  module: Module;
  origin: Origin;
}

export interface ArmouryShelves {
  /** The library's templates and effects, as the shelves list them (ids `lib-<asset id>`). */
  areas: AreaPreset[];
  effects: EffectPreset[];
  /** The library's modules, for the forge (plan armoury, stage 5). */
  modules: ShelvedModule[];
  /** The library's kits, for the Armory's kit shelf (part 6); ids `lib-<asset id>`. */
  loadouts: Loadout[];
  saveArea(name: string, area: WeaponArea): void;
  saveEffect(kind: EffectKind, spec: EffectPreset['spec']): void;
  saveModule(module: Module): void;
  saveLoadout(loadout: Loadout): void;
}

type Shelved = 'area' | 'effect' | 'module' | 'loadout';
type Read = AreaPreset | EffectPreset | ShelvedModule | Loadout;
const SHELVED: readonly Shelved[] = ['area', 'effect', 'module', 'loadout'];
// Saved at least once (`version` 0 is an asset just made, with nothing to read yet - the fuzz found the shelf logging
// "Version 0 is not in your library" for each, 2026-10-24), and not in the trash.
const live = (kind: Shelved) => (a: AssetRow) => a.kind === kind && !a.deletedAt && a.version > 0;
const keyOf = (a: AssetRow) => `${a.id}@${a.version}`;

/** Read what the cache lacks; the rest is kept. Unreadable assets are logged and left off the shelf. */
async function readAhead(access: LibraryAccess, rows: AssetRow[], cache: Map<string, Read>) {
  for (const row of rows) {
    if (cache.has(keyOf(row))) continue;
    try {
      cache.set(keyOf(row), await presetOf(access, row));
    } catch (err) {
      diag.error('assets', `the shelf could not read ${row.name}`, errorSummary(err));
    }
  }
}

async function presetOf(access: LibraryAccess, row: AssetRow): Promise<Read> {
  const id = `lib-${row.id}`;
  // A kit is shown by the library's name for it; its own id is the asset's, so the shelf can tell it apart.
  if (row.kind === 'loadout') return { ...(await readArmouryAsset(access, row, 'loadout')), id, name: row.name };
  if (row.kind === 'module') {
    const module = await readArmouryAsset(access, row, 'module');
    return { id, name: row.name, module, origin: { assetId: row.id, version: row.version } };
  }
  if (row.kind === 'area') return { id, name: row.name, area: (await readArmouryAsset(access, row, 'area')).area };
  const { kind, spec } = await readArmouryAsset(access, row, 'effect');
  // The shelf shows an effect by its spec's name: the asset's name is what the library calls it.
  return { id, kind, spec: { ...spec, name: row.name } } as EffectPreset;
}

export default function useArmouryShelves(
  access: LibraryAccess | null,
  label: string,
  toast: (text: string, opts?: { type?: 'info' | 'error' | 'success' }) => void,
): ArmouryShelves | null {
  const cache = useRef(new Map<string, Read>());
  const [, setRead] = useState(0);
  const rows = useMemo(
    () => (access ? access.lib.assets.filter((a) => SHELVED.some((kind) => live(kind)(a))) : []),
    [access],
  );
  // Re-read on what the library holds (its rows' ids and versions), not on the library object's identity.
  const signature = rows.map(keyOf).join('|');
  const now = useRef({ access, rows });
  now.current = { access, rows };
  useEffect(() => {
    const { access: at, rows: these } = now.current;
    if (!at) return;
    let gone = false;
    void readAhead(at, these, cache.current).then(() => !gone && setRead((n) => n + 1));
    return () => {
      gone = true;
    };
  }, [signature]);
  const say = useStableCallback(toast);
  // THE SAME SHELVES until the library or what has been read of it changes (plan shell-contexts, A3): rebuilt on
  // every render, they gave the roster context a new value each time.
  const reads = useShelfReads(rows, cache.current);
  return useMemo(() => (access ? shelvesOf(access, reads, label, say) : null), [access, reads, label, say]);
}

/** Each row's read, the same list until a row's id or version changes or its read lands. */
function useShelfReads(rows: AssetRow[], cache: Map<string, Read>): (Read | undefined)[] {
  const entries = useStableList(
    rows.map((r) => ({ key: keyOf(r), read: cache.get(keyOf(r)) })),
    (e) => `${e.key}:${e.read ? 'read' : 'waiting'}`,
  );
  return useMemo(() => entries.map((e) => e.read), [entries]);
}

/** The shelves from what has been read of the rows, and the verbs that save to the library. */
function shelvesOf(
  access: LibraryAccess,
  reads: (Read | undefined)[],
  label: string,
  toast: (text: string, opts?: { type?: 'info' | 'error' | 'success' }) => void,
): ArmouryShelves {
  const shelf = reads.filter((p): p is Read => p !== undefined);
  const save = (kind: Shelved, name: string, text: string) =>
    newFromTable(access, kind, name, text, label).then(
      (saved) => toast(`Saved to your library as "${saved.name}", in From session ${label}.`, { type: 'success' }),
      (err: Error) => toast(err.message, { type: 'error' }),
    );
  return {
    areas: shelf.filter((p): p is AreaPreset => 'area' in p),
    effects: shelf.filter((p): p is EffectPreset => 'spec' in p),
    modules: shelf.filter((p): p is ShelvedModule => 'module' in p),
    loadouts: shelf.filter((p): p is Loadout => 'weapons' in p),
    saveArea: (name, area) => void save('area', name, KINDS.area.encode({ area })),
    saveEffect: (kind, spec) => void save('effect', spec.name, KINDS.effect.encode({ kind, spec })),
    saveModule: (module) => void save('module', module.name, KINDS.module.encode(module)),
    saveLoadout: (loadout) => void save('loadout', loadout.name, KINDS.loadout.encode(loadout)),
  };
}
